import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { apiRequest } from '../modules/shared/hook/useApi';
import { IBookingEvent } from '../types';
import { LikeMindedState } from './store';

interface BookingsSlice {
  createBookingLoading: boolean;
  loading?: boolean;
  fetched: boolean;
  bookings: Array<IBookingEvent>;
}

interface CreateBooking {
  offeringId: string;
  recommendationId?: string;
  filters: {
    language?: string;
    topic?: string;
    format?: string;
  };
  utmCampaign?: string;
}

const initialState = {
  createBookingLoading: false,
  loading: undefined,
  fetched: false,
  bookings: [],
} as BookingsSlice;

export const fetchBookingsHistory = createAsyncThunk<
  Array<IBookingEvent>,
  void,
  {
    rejectValue: number;
  }
>('bookings/history', async (_, { rejectWithValue }) => {
  try {
    const response = await apiRequest<Array<IBookingEvent>>(
      'getBookingHistory',
    );
    return response?.map((obj: any, index: number) => ({ ...obj, id: index }));
  } catch (e) {
    Sentry.captureException(e);
    return rejectWithValue(401);
  }
});

export const createBooking = createAsyncThunk<
  CreateBooking,
  CreateBooking,
  {
    rejectValue: number;
  }
>(
  'bookings/create',
  async ({ offeringId, recommendationId, filters }, { rejectWithValue }) => {
    try {
      const response = await apiRequest<CreateBooking>('createBooking', {
        offeringId,
        recommendationId,
        filters,
      });

      return response;
    } catch (e) {
      Sentry.captureException(e);
      return rejectWithValue(401);
    }
  },
);

export const bookingsHistorySlice = createSlice({
  name: 'bookingsHistory',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(createBooking.pending, (state) => {
      state.createBookingLoading = true;
    });
    builder.addCase(createBooking.fulfilled, (state) => {
      state.createBookingLoading = false;
    });
    builder.addCase(createBooking.rejected, (state) => {
      state.createBookingLoading = false;
    });
    builder.addCase(fetchBookingsHistory.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchBookingsHistory.fulfilled, (state, action) => {
      state.loading = false;
      state.fetched = true;
      state.bookings = action.payload;
    });
    builder.addCase(fetchBookingsHistory.rejected, (state) => {
      state.loading = false;
      state.fetched = true;
      state.bookings = [];
    });
  },
});

export const bookingsHistoryReducer = bookingsHistorySlice.reducer;
export const selectBookingsHistory = (state: LikeMindedState) =>
  state.bookingsHistory;
